import bloomlifeLogoUrl from "@/assets/bloomlife-logo-full.svg"
import measurementImageUrl from "@/assets/measurement.svg"
import BackButton from "@/components/BackButton"
import HeroImage from "@/components/HeroImage"
import Page from "@/components/Page"
import Spinner from "@/components/Spinner"
import { Button } from "@/components/chadcn/Button"
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/chadcn/Dialog"
import { useLogOutMutation, useSelfQuery, useUpdatePatientProfileMutation } from "@/data/queries"
import { DeviceDistributionMethod } from "@/model/DeviceDistributionMethod"
import { toPatientAppStateApiDto } from "@/model/PatientAppState"
import { ROUTES } from "@/routes"
import { useState } from "react"
import { Navigate, useNavigate, useSearchParams } from "react-router"

const IGLUCOSE_SETUP_YOUTUBE_URL = "https://youtube.com/embed/ERUO1TRr6Bk?autoplay=0"
const IGLUCOSE_PLUS_SETUP_YOUTUBE_URL = "https://www.youtube.com/embed/3NUHW1zTsh8?autoplay=0"
const ONBOARDING_INTRODUCTION_VIMEO_URL = "https://player.vimeo.com/video/949182782"

const SETUP_STEPS = ["welcome", "get-ready", "videos"] as const
type SetupStep = (typeof SETUP_STEPS)[number]

const isValidStep = (value: string | null): value is SetupStep =>
  SETUP_STEPS.includes(value as SetupStep)

// --- Individual step components ---

const BloodGlucoseInfoStep = ({ onBack, onNext }: { onBack: () => void; onNext: () => void }) => (
  <div className="flex min-h-[calc(100vh-32px)] flex-col gap-6">
    <BackButton customClickHandler={onBack} />
    <div className="flex flex-1 flex-col gap-4">
      <h1 className="text-h1 text-primary-contrast">
        Understanding blood glucose and its vital role in your body
      </h1>
      <p className="text-base text-primary-contrast">
        Glucose is a type of sugar found in the bloodstream. Blood glucose is commonly referred to
        as blood sugar. Glucose is the primary source of energy for all of the cells in your body,
        especially your brain and muscles
      </p>
      <p className="text-base text-primary-contrast">
        When you take a blood glucose measurement, the number you see reflects how much glucose is
        in your bloodstream at that moment
      </p>
      <div className="aspect-video w-full overflow-hidden rounded-lg border border-secondary bg-black">
        <iframe
          allowFullScreen
          src={ONBOARDING_INTRODUCTION_VIMEO_URL}
          title="Welcome to Bloomlife Connects"
          className="h-full w-full border-0"
        />
      </div>
    </div>
    <Button type="button" className="w-full py-2 text-base" onClick={onNext}>
      Continue
    </Button>
  </div>
)

const NoDeviceStep = ({
  deviceDistributionMethod,
}: {
  deviceDistributionMethod: DeviceDistributionMethod
}) => {
  const navigate = useNavigate()
  return (
    <div className="flex min-h-[calc(100vh-32px)] flex-col justify-between gap-6">
      <div className="flex flex-col gap-4">
        <HeroImage src={measurementImageUrl} alt="health-measurement" />
        <h1 className="text-h1 text-primary-contrast">
          We see you don&apos;t have a blood glucose monitor connected yet
        </h1>
        <h2 className="text-h2 text-primary-contrast/70">
          {deviceDistributionMethod === "from-provider"
            ? "Scan the QR code on the box"
            : "Check your inbox for shipping info, or contact Bloomlife"}
        </h2>
      </div>
      <Button
        type="button"
        className="w-full py-2 text-base"
        onClick={() => navigate(ROUTES.CONTACT_BLOOMLIFE)}
      >
        Contact Bloomlife
      </Button>
    </div>
  )
}

const SkipSetupModal = ({ open, onClose }: { open: boolean; onClose: () => void }) => {
  const navigate = useNavigate()
  const { mutate: logOut, isPending } = useLogOutMutation()

  const handleLogOut = () => {
    logOut(undefined, {
      onSuccess: () => navigate(ROUTES.SIGN_IN, { replace: true }),
    })
  }

  return (
    <Dialog open={open} onOpenChange={(isOpen) => !isOpen && onClose()}>
      <DialogContent className="max-w-[320px]">
        <DialogHeader>
          <DialogTitle className="px-8 text-center text-h4">
            Whenever you&apos;re ready to proceed, log back in!
          </DialogTitle>
        </DialogHeader>
        <Button
          type="button"
          className="flex w-full gap-3 py-2 text-base"
          disabled={isPending}
          onClick={handleLogOut}
        >
          {isPending && <Spinner />}
          OK
        </Button>
      </DialogContent>
    </Dialog>
  )
}

const DevicePrepStep = ({ onNext }: { onNext: () => void }) => {
  const [isSkipModalOpen, setIsSkipModalOpen] = useState(false)

  return (
    <div className="flex min-h-[calc(100vh-32px)] flex-col justify-between gap-6">
      <div className="flex flex-col items-center gap-6">
        <HeroImage src={measurementImageUrl} alt="health-measurement" />
        <img alt="logo" src={bloomlifeLogoUrl} width="230" />
        <h2 className="text-center text-h2 text-primary-contrast">
          Ready to set up your connected blood glucose monitor?
          <br />
          <br />
          Make sure you have the monitor on hand for the next few steps
        </h2>
      </div>
      <div className="flex flex-col gap-3">
        <Button type="button" className="w-full py-2 text-base" onClick={onNext}>
          Let&apos;s go!
        </Button>
        <Button
          type="button"
          variant="outline"
          className="w-full py-2 text-base"
          onClick={() => setIsSkipModalOpen(true)}
        >
          Not ready yet
        </Button>
      </div>
      <SkipSetupModal open={isSkipModalOpen} onClose={() => setIsSkipModalOpen(false)} />
    </div>
  )
}

const SetupVideosStep = ({ onDone, isDone }: { onDone: () => void; isDone: boolean }) => (
  <div className="flex min-h-[calc(100vh-32px)] flex-col gap-6">
    <div className="flex flex-1 flex-col gap-4">
      <h1 className="text-h1 text-primary-contrast">
        Setting up your connected blood glucose monitor
      </h1>
      <p className="text-base text-primary-contrast">
        Please follow the instructions below depending on the device you have
      </p>
      <section className="flex flex-col gap-2">
        <h2 className="text-center text-base font-semibold text-primary-contrast">iGlucose</h2>
        <div className="aspect-video w-full overflow-hidden rounded-lg border border-secondary bg-black">
          <iframe
            allowFullScreen
            src={IGLUCOSE_SETUP_YOUTUBE_URL}
            title="iGlucose setup instructions"
            className="h-full w-full border-0"
          />
        </div>
      </section>
      <section className="flex flex-col gap-2">
        <h2 className="text-center text-base font-semibold text-primary-contrast">iGlucose Plus</h2>
        <div className="aspect-video w-full overflow-hidden rounded-lg border border-secondary bg-black">
          <iframe
            allowFullScreen
            src={IGLUCOSE_PLUS_SETUP_YOUTUBE_URL}
            title="iGlucose Plus setup instructions"
            className="h-full w-full border-0"
          />
        </div>
      </section>
    </div>
    <Button
      type="button"
      className="flex w-full gap-3 py-2 text-base"
      disabled={isDone}
      onClick={onDone}
    >
      {isDone && <Spinner />}
      Continue
    </Button>
  </div>
)

// --- Main page ---

const BloodGlucoseDeviceSetupPage = () => {
  const [searchParams, setSearchParams] = useSearchParams()
  const navigate = useNavigate()
  const stepParam = searchParams.get("step")

  const { data: self } = useSelfQuery()
  const { mutate: updatePatientProfile, isPending: savingProfile } =
    useUpdatePatientProfileMutation()

  if (!isValidStep(stepParam)) {
    return <Navigate to="/blood-glucose-device-setup?step=welcome" replace />
  }

  const step = stepParam

  if (!self) {
    return (
      <Page showAppbar={false}>
        <div className="flex justify-center py-12">
          <Spinner />
        </div>
      </Page>
    )
  }

  const goToStep = (next: SetupStep) => setSearchParams({ step: next })

  // Backing out of device setup un-finishes the profile so the patient
  // re-enters onboarding on their next visit (same contract as the old app).
  const handleWelcomeBack = () => {
    if (self.patientAppState.profileFormFilled) {
      updatePatientProfile(
        {
          patientId: self.id,
          profile: {
            patientAppState: toPatientAppStateApiDto({
              profileFormFilled: false,
              bloodGlucoseDeviceSetupCompleted:
                self.patientAppState.bloodGlucoseDeviceSetupCompleted,
            }),
          },
        },
        {
          onSettled: () => navigate(-1),
        },
      )
    } else {
      navigate(-1)
    }
  }

  const handleDone = () => {
    updatePatientProfile(
      {
        patientId: self.id,
        profile: {
          patientAppState: toPatientAppStateApiDto({
            profileFormFilled: self.patientAppState.profileFormFilled,
            bloodGlucoseDeviceSetupCompleted: true,
          }),
        },
      },
      {
        onSuccess: () =>
          navigate(ROUTES.DASHBOARD, { replace: true, state: { initialLogin: true } }),
        onError: () => navigate(ROUTES.DASHBOARD, { replace: true, state: { initialLogin: true } }),
      },
    )
  }

  return (
    <Page showAppbar={false}>
      {step === "welcome" && (
        <BloodGlucoseInfoStep onBack={handleWelcomeBack} onNext={() => goToStep("get-ready")} />
      )}
      {step === "get-ready" &&
        (self.hasBloodGlucoseMonitor ? (
          <DevicePrepStep onNext={() => goToStep("videos")} />
        ) : (
          <NoDeviceStep deviceDistributionMethod={self.deviceDistributionMethod} />
        ))}
      {step === "videos" && <SetupVideosStep onDone={handleDone} isDone={savingProfile} />}
    </Page>
  )
}

export default BloodGlucoseDeviceSetupPage
